/**
 * configuracao.ts — identidade visual e etapas de cada tipo de geração.
 *
 * Portado 1:1 dos 9 templates *_status.html (título, subtítulo, ícone,
 * gradiente e a lista de fases de cada domínio). As classes são literais:
 * o Tailwind da SPA é build local com purge, então `from-${cor}-500` não
 * sobrevive ao bundle.
 */

import {
  faBalanceScale,
  faClipboardList,
  faFileContract,
  faIdCard,
  faProjectDiagram,
  faScroll,
  faFileMedical,
  faSearchDollar,
  faShieldAlt,
  type IconDefinition,
} from '@fortawesome/free-solid-svg-icons'
import type { TipoTask } from '../../api/tasks'

export interface ConfiguracaoTipo {
  titulo: string
  subtitulo: string
  rotulo: string // nome curto (rastreador, mensagens)
  icone: IconDefinition
  cabecalho: string // gradiente do header
  subtituloCor: string
  barra: string // gradiente da barra de progresso
  acento: string // cor do texto do percentual
  botao: string // gradiente do botão "Atualizar"
  fases: string[]
  mensagemInicial: string
  rotuloVisualizar: string
}

export const CONFIGURACAO: Record<TipoTask, ConfiguracaoTipo> = {
  dfd: {
    titulo: 'Status da Geração do DFD',
    subtitulo: 'Acompanhe o progresso da geração do Documento de Formalização da Demanda',
    rotulo: 'DFD',
    icone: faProjectDiagram,
    cabecalho: 'from-indigo-500 via-purple-600 to-blue-500',
    subtituloCor: 'text-indigo-200',
    barra: 'from-indigo-500 via-purple-600 to-blue-500',
    acento: 'text-indigo-600 dark:text-indigo-400',
    botao: 'from-indigo-500 to-purple-600',
    fases: ['Análise da necessidade', 'Definição de especificações',
      'Estimativa de quantidades', 'Elaboração do DFD com IA', 'Validação final'],
    mensagemInicial: 'Iniciando geração do DFD...',
    rotuloVisualizar: 'Visualizar DFD',
  },
  etp: {
    titulo: 'Status da Geração do ETP',
    subtitulo: 'Acompanhe o progresso da geração do Estudo Técnico Preliminar',
    rotulo: 'ETP',
    icone: faClipboardList,
    cabecalho: 'from-amber-500 via-orange-500 to-yellow-500',
    subtituloCor: 'text-amber-100',
    barra: 'from-amber-500 via-orange-500 to-yellow-500',
    acento: 'text-amber-600 dark:text-amber-400',
    botao: 'from-amber-500 to-yellow-500',
    fases: ['Carregando dados do DFD', 'Pesquisa de preços via API',
      'Gerando seções com IA', 'Validando documento', 'Finalizando e salvando'],
    mensagemInicial: 'Iniciando geração do ETP...',
    rotuloVisualizar: 'Visualizar ETP',
  },
  tr: {
    titulo: 'Status da Geração do TR',
    subtitulo: 'Acompanhe o progresso da geração do Termo de Referência',
    rotulo: 'Termo de Referência',
    icone: faFileContract,
    cabecalho: 'from-emerald-500 via-teal-600 to-cyan-500',
    subtituloCor: 'text-emerald-100',
    barra: 'from-emerald-500 via-teal-600 to-cyan-500',
    acento: 'text-emerald-600 dark:text-emerald-400',
    botao: 'from-emerald-500 to-cyan-500',
    fases: ['Análise do ETP aprovado', 'Geração das 10 alíneas obrigatórias',
      'Validação Lei 14.133/2021', 'Integração de pesquisa de preços',
      'Montagem do documento'],
    mensagemInicial: 'Iniciando geração do TR...',
    rotuloVisualizar: 'Visualizar TR',
  },
  edital: {
    titulo: 'Status da Geração do Edital',
    subtitulo: 'Acompanhe o progresso da geração do Edital de Licitação',
    rotulo: 'Edital',
    icone: faScroll,
    cabecalho: 'from-purple-500 via-indigo-600 to-violet-500',
    subtituloCor: 'text-purple-100',
    barra: 'from-purple-500 via-indigo-600 to-violet-500',
    acento: 'text-purple-600 dark:text-purple-400',
    botao: 'from-purple-500 to-violet-500',
    fases: ['Consolidando dados do TR e ETP',
      'Gerando seções do Edital (14 seções TCU)',
      'Aplicando regras da Lei 14.133/2021', 'Validando conformidade legal',
      'Finalizando documento'],
    mensagemInicial: 'Iniciando geração do Edital...',
    rotuloVisualizar: 'Visualizar Edital',
  },
  credenciamento: {
    titulo: 'Status do Edital de Chamamento',
    subtitulo: 'Acompanhe a geração do Edital de Chamamento Público de Credenciamento',
    rotulo: 'Edital de Chamamento',
    icone: faIdCard,
    cabecalho: 'from-teal-500 via-cyan-600 to-teal-500',
    subtituloCor: 'text-teal-100',
    barra: 'from-teal-500 via-cyan-600 to-teal-500',
    acento: 'text-teal-600 dark:text-teal-400',
    botao: 'from-teal-500 to-cyan-600',
    fases: ['Consolidando dados do DFD, ETP e TR',
      'Gerando seções do chamamento público',
      'Aplicando o Art. 79 da Lei 14.133/2021', 'Validando conformidade legal',
      'Finalizando documento'],
    mensagemInicial: 'Iniciando geração do chamamento...',
    rotuloVisualizar: 'Visualizar Chamamento',
  },
  auditoria: {
    titulo: 'Status da Auditoria',
    subtitulo: 'Acompanhe o progresso da auditoria dos documentos',
    rotulo: 'Auditoria',
    icone: faSearchDollar,
    cabecalho: 'from-rose-500 via-orange-500 to-amber-500',
    subtituloCor: 'text-rose-100',
    barra: 'from-rose-500 via-orange-500 to-amber-500',
    acento: 'text-rose-600 dark:text-rose-400',
    botao: 'from-rose-500 to-amber-500',
    fases: ['Extração de texto dos documentos', 'Detecção do tipo de documento',
      'Análise com IA especializada', 'Pesquisa jurisprudencial direcionada',
      'Geração do relatório final'],
    mensagemInicial: 'Iniciando auditoria...',
    rotuloVisualizar: 'Ver Relatório',
  },
  impugnacao: {
    titulo: 'Gerando Impugnação',
    subtitulo: 'Acompanhe o progresso da geração da impugnação',
    rotulo: 'Impugnação',
    icone: faFileContract,
    cabecalho: 'from-blue-500 via-indigo-600 to-violet-500',
    subtituloCor: 'text-blue-100',
    barra: 'from-blue-500 via-indigo-600 to-violet-500',
    acento: 'text-blue-600 dark:text-blue-400',
    botao: 'from-blue-500 to-violet-500',
    fases: ['Situational Awareness', 'Active Clarification',
      'Semantic Router - Estratégia', 'Task Decomposition - Planejamento',
      'Reasoning Core - Processamento', 'Code Execution - Cálculos',
      'Montagem do HTML final'],
    mensagemInicial: 'Iniciando geração da impugnação...',
    rotuloVisualizar: 'Visualizar Impugnação',
  },
  defesa: {
    titulo: 'Gerando Defesa',
    subtitulo: 'Acompanhe o progresso da geração da defesa jurídica',
    rotulo: 'Defesa',
    icone: faShieldAlt,
    cabecalho: 'from-emerald-500 via-green-600 to-teal-500',
    subtituloCor: 'text-emerald-100',
    barra: 'from-emerald-500 via-green-600 to-teal-500',
    acento: 'text-emerald-600 dark:text-emerald-400',
    botao: 'from-emerald-500 to-teal-500',
    fases: ['Extração de argumentos da impugnação',
      'Análise de pontos fortes do edital', 'Classificação de dificuldade',
      'Busca de fundamentação jurídica', 'Geração da peça de defesa',
      'Pipeline agêntico (Self-Reflection, CoVe)'],
    mensagemInicial: 'Iniciando geração da defesa...',
    rotuloVisualizar: 'Visualizar Defesa',
  },
  parecer: {
    titulo: 'Gerando Parecer Técnico SUS',
    subtitulo: 'Aguarde enquanto o parecer é gerado pela IA',
    rotulo: 'Parecer SUS',
    icone: faFileMedical,
    cabecalho: 'from-teal-600 via-cyan-600 to-teal-500',
    subtituloCor: 'text-teal-100',
    barra: 'from-teal-600 via-cyan-600 to-teal-500',
    acento: 'text-teal-600 dark:text-teal-400',
    botao: 'from-teal-600 to-cyan-600',
    fases: ['Analisando a solicitação', 'Estruturando o parecer',
      'Consultando a base normativa (RAG)', 'Consultando fontes oficiais',
      'Redigindo o parecer com IA', 'Validando citações e conclusão'],
    mensagemInicial: 'Preparando a solicitação...',
    rotuloVisualizar: 'Ver Parecer',
  },
  pesquisa: {
    titulo: 'Status da Pesquisa Jurídica',
    subtitulo: 'Acompanhe o progresso da pesquisa jurisprudencial',
    rotulo: 'Pesquisa Jurídica',
    icone: faBalanceScale,
    cabecalho: 'from-violet-600 via-purple-600 to-indigo-600',
    subtituloCor: 'text-violet-200',
    barra: 'from-violet-600 via-purple-600 to-indigo-600',
    acento: 'text-violet-600 dark:text-violet-400',
    botao: 'from-violet-600 to-indigo-600',
    fases: ['Análise da consulta jurídica', 'Busca em bases de jurisprudência',
      'Análise de precedentes relevantes', 'Síntese dos resultados',
      'Geração do relatório final'],
    mensagemInicial: 'Iniciando pesquisa...',
    rotuloVisualizar: 'Ver Pesquisa',
  },
}

/** Fase N acesa a partir de `limiar`; "em andamento" nos 15 pontos anteriores
 *  — mesma regra do updatePhases() dos templates. */
export function limiarDaFase(indice: number, total: number): number {
  const LIMIARES_5: readonly number[] = [15, 35, 55, 80, 95]
  const limiar = total === LIMIARES_5.length ? LIMIARES_5[indice] : undefined
  return limiar ?? Math.round(((indice + 1) / total) * 95)
}

export type EstadoFase = 'concluida' | 'andamento' | 'pendente'

export function estadoDaFase(
  indice: number,
  total: number,
  percentual: number,
): EstadoFase {
  const limiar = limiarDaFase(indice, total)
  if (percentual >= limiar) return 'concluida'
  if (percentual >= limiar - 15) return 'andamento'
  return 'pendente'
}
